<template>
  <div :class="{ 'full-height q-pb-lg': $q.screen.lt.md && !clean && !iconOnly }">
    <div
      v-if="showTrailerButton && !$q.screen.lt.md"
      class="row buttons q-mt-md"
      :class="'q-gutter-' + gutterSize"
    >
      <q-btn
        v-if="showLabels"
        class="watch-trailer-btn"
        icon="play_arrow"
        :label="$t('movie.actions.watchTrailer')"
        @click.stop="playTrailer"
      />
    </div>

    <div
      class="buttons"
      :class="[`q-gutter-${gutterSize}`,
               $q.screen.lt.md ? 'items-start' : 'justify-evenly',
               !showLabels ? 'row' : clean ? 'row' : 'column full-height q-my-md justify-between',
               vertical ? 'column' : 'row']"
    >
      <transition
        :leave-active-class="preventAnimations ? '' : 'animated fadeOutRight'"
        :enter-active-class="preventAnimations ? '' : 'animated fadeInRight'"
      >
        <div
          v-if="!forceHide && showUserButtons && (!activeOnly || watchListed) && (!watched || watchListed)"
          :class="$q.screen.lt.md ? 'col full-width q-py-sm' : ''"
        >
          <q-btn
            v-if="showLabels"
            :loading="watchListLoading"
            full-width
            no-caps
            unelevated
            dense
            color="transparent"
            :text-color="useDarkMode ? 'white' : 'black'"
            :class="{ 'full-width': $q.screen.lt.md }"
            @click.stop="toggleList('watch_list')"
          >
            <div
              class="items-center"
              :class="$q.screen.lt.md ? '' : 'row no-wrap'"
            >
              <q-icon
                :class="{
                  'show-label': true,
                  clean: clean & !watchListed
                }"
                :name="watchListed ? 'bookmark' : 'bookmark_outline'"
                :size="size"
                :color="watchListed ? 'red' : useDarkMode ? 'white' : undefined"
              />
              <div
                :class="$q.screen.lt.md ? 'text-caption' : 'button-label text-left'"
              >
                <span v-if="!watchListed">
                  {{ $t('user.actions.addToWatchList') }}
                </span>
                <span v-else>
                  {{ $t('user.watchList') }}
                </span>
              </div>
            </div>
          </q-btn>

          <q-btn
            v-else
            flat
            round
            dense
            class="transparent"
            :class="{
              clean: clean & !watchListed,
              'watch-listed': watchListed,
              'text-white': useDarkMode
            }"
            :icon="watchListed ? 'bookmark' : 'bookmark_outline'"
            :size="size"
            :loading="watchListLoading"
            @click.stop="toggleList('watch_list')"
          >
            <q-tooltip v-if="!showLabels">
              <span v-if="!loggedIn">
                Log In to
              </span>
              <span v-if="!watchListed">
                {{ $t('user.actions.addToWatchList') }}
              </span>
              <span v-else>
                {{ $t('user.actions.removeFromWatchList') }}
              </span>
            </q-tooltip>
          </q-btn>
        </div>
      </transition>

      <transition
        :leave-active-class="preventAnimations ? '' : 'animated fadeOutRight'"
        :enter-active-class="preventAnimations ? '' : 'animated fadeInRight'"
      >
        <div
          v-if="!forceHide && showUserButtons && !hideRating && (!activeOnly || rated || ratingMenu || ratingLoading)"
          :class="$q.screen.lt.md ? 'col full-width q-py-sm' : ''"
        >
          <q-btn
            v-if="showLabels"
            no-caps
            unelevated
            dense
            color="transparent"
            :text-color="useDarkMode ? 'white' : 'black'"
            :class="{ 'full-width': $q.screen.lt.md }"
            :loading="ratingLoading"
            @click.stop="rated ? toggleList('rating') : null"
          >
            <div
              class="items-center"
              :class="$q.screen.lt.md ? '' : 'row no-wrap'"
            >
              <q-icon
                :class="{
                  'show-label': true,
                  clean: clean & !rated
                }"
                :name="rated ? 'star' : 'star_outline'"
                :size="size"
                :color="rated ? 'amber' : useDarkMode ? 'white' : undefined"
              />
              <q-badge
                v-if="rated"
                class="absolute"
                :class="$q.screen.lt.md ? 'q-ml-none' : 'q-ml-md q-mb-md'"
                color="grey-9"
                :style="{ 'transform': $q.screen.lt.md ? 'scale(1)' : 'scale(0.8)' }"
              >
                {{ rating }}
              </q-badge>
              <div
                :class="$q.screen.lt.md ? 'text-caption' : 'button-label'"
              >
                <span v-if="!rated">
                  {{ $t('movie.actions.rate') }}
                </span>
                <span v-else>
                  {{ $t('movie.actions.rated') }}
                </span>
              </div>
              <q-menu
                v-if="!rated"
                v-model="ratingMenu"
                class="flat rating"
                anchor="bottom middle"
                self="top middle"
                :offset="[0, 10]"
              >
                <q-rating
                  :model-value="rating"
                  class="q-pa-xs"
                  style="z-index: 2; background: transparent"
                  max="5"
                  size="3.5em"
                  color="yellow"
                  icon="star_border"
                  icon-selected="star"
                  icon-half="star_half"
                  flat
                  no-dimming
                  @update:model-value="setRating($event)"
                />
              </q-menu>
            </div>
          </q-btn>

          <q-btn
            v-else
            flat
            round
            dense
            class="transparent"
            :class="{
              clean: clean & !rated,
              rated: rated,
              'text-white': useDarkMode
            }"
            :icon="rated ? 'star' : 'star_outline'"
            :size="size"
            :loading="ratingLoading"
            @click.stop="rated ? toggleList('rating') : null"
          >
            <q-badge
              v-if="rated"
              class="absolute q-ml-lg q-mb-lg"
              color="dark"
              style="transform: scale(0.8)"
            >
              {{ rating }}
            </q-badge>
            <q-menu
              v-else
              v-model="ratingMenu"
              class="flat rating"
              anchor="top middle"
              self="bottom middle"
              :offset="[0, 10]"
            >
              <q-rating
                :model-value="rating"
                class="q-pa-xs"
                style="z-index: 2; background: transparent"
                max="5"
                size="3.5em"
                color="yellow"
                icon="star_border"
                icon-selected="star"
                icon-half="star_half"
                flat
                no-dimming
                @update:model-value="setRating($event)"
              />
            </q-menu>
            <q-tooltip v-if="!showLabels && !ratingMenu">
              {{ $t('auth.actions.loginToDo', { action: rated ? $t('movie.actions.removeRating', { rating }) : $t('movie.actions.rate') }) }}
            </q-tooltip>
          </q-btn>
        </div>
      </transition>

      <transition
        :leave-active-class="preventAnimations ? '' : 'animated fadeOutRight'"
        :enter-active-class="preventAnimations ? '' : 'animated fadeInRight'"
      >
        <div
          v-if="!forceHide && showUserButtons && !hideFavorite && (!activeOnly || favorited)"
          :class="$q.screen.lt.md ? 'col full-width q-py-sm' : ''"
        >
          <q-btn
            v-if="showLabels"
            no-caps
            unelevated
            dense
            color="transparent"
            :text-color="useDarkMode ? 'white' : 'black'"
            :class="{ 'full-width': $q.screen.lt.md }"
            :loading="favoriteLoading"
            @click.stop="toggleList('favorite')"
          >
            <div
              class="items-center"
              :class="$q.screen.lt.md ? '' : 'row no-wrap'"
            >
              <q-icon
                :class="{
                  'show-label': true,
                  clean: clean & !favorited
                }"
                :name="favorited ? 'favorite' : 'favorite_outline'"
                :size="size"
                :color="favorited ? 'pink-6' : useDarkMode ? 'white' : undefined"
              />
              <div
                :class="$q.screen.lt.md ? 'text-caption' : 'button-label text-left'"
              >
                <span v-if="!favorited">
                  {{ $t('user.actions.addToFavorites') }}
                </span>
                <span v-else>
                  {{ $tc('common.favorite', 1) }}
                </span>
              </div>
            </div>
          </q-btn>

          <q-btn
            v-else
            flat
            round
            dense
            class="transparent"
            :class="{
              clean: clean & !favorited,
              favorited: favorited,
              'text-white': useDarkMode
            }"
            :icon="favorited ? 'favorite' : 'favorite_outline'"
            :size="size"
            :loading="favoriteLoading"
            @click.stop="toggleList('favorite')"
          >
            <q-tooltip v-if="!showLabels">
              <span v-if="!loggedIn">
                {{ $t('auth.actions.loginToDo') }}
              </span>
              <span v-if="!favorited">
                {{ $t('user.actions.addToFavorites') }}
              </span>
              <span v-else>
                {{ $t('user.actions.removeFromFavorites') }}
              </span>
            </q-tooltip>
          </q-btn>
        </div>
      </transition>

      <transition
        :leave-active-class="preventAnimations ? '' : 'animated fadeOutRight'"
        :enter-active-class="preventAnimations ? '' : 'animated fadeInRight'"
      >
        <div
          v-if="!forceHide && showUserButtons && watched"
          :class="$q.screen.lt.md ? 'col full-width q-py-sm' : ''"
        >
          <q-btn
            v-if="showLabels"
            full-width
            no-caps
            unelevated
            dense
            :ripple="false"
            color="transparent"
            :text-color="useDarkMode ? 'white' : 'black'"
            :class="{ 'full-width': $q.screen.lt.md }"
          >
            <div
              class="items-center"
              :class="$q.screen.lt.md ? '' : 'row no-wrap'"
            >
              <q-icon
                :class="{
                  'show-label': true,
                  clean: clean,
                  'text-white': useDarkMode
                }"
                name="visibility"
                :size="size"
              />
              <div
                :class="$q.screen.lt.md ? 'text-caption' : 'button-label'"
              >
                <span>
                  {{ $t('actions.watched') }}
                </span>
              </div>
            </div>
            <q-tooltip>
              {{ $t('movie.youHaveSeenThisMovie') }}
            </q-tooltip>
          </q-btn>

          <q-btn
            v-else
            name="visibility"
            style="cursor: default;"
            round
            dense
            :ripple="false"
            class="transparent"
            :class="{
              clean: clean,
              'text-white': useDarkMode
            }"
            icon="visibility"
            :size="size"
          >
            <q-tooltip>
              {{ $t('movie.youHaveSeenThisMovie') }}
            </q-tooltip>
          </q-btn>
        </div>
      </transition>

      <q-btn
        v-if="!showLabels && showTrailerButton && !$q.screen.lt.md"
        class="watch-trailer-btn"
        icon="play_arrow"
        :label="$t('movie.actions.watchTrailer')"
        @click.stop="playTrailer"
      />
      <div
        v-if="$q.screen.lt.md && showTrailerButton"
        class="items-center  q-pt-sm col-4"
        :class="clean ? 'q-pr-md' : 'q-mx-auto'"
      >
        <q-btn
          rounded
          :padding="clean ? 'xs' : 'sm'"
          size="sm"
          color="primary"
          :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
          style="border-radius: 50px; font-weight: bolder; width: 100%; min-width: 80px; min-height: 55px;"
          :style="clean ? 'padding-left: 40px;' : 'height: 100%; padding-top: 60px;'"
          class="row"
          @click.stop="playTrailer"
        >
          <q-avatar
            class="q-ma-xs"
            icon="play_arrow"
            color="white"
            text-color="primary"
            :class="clean ? 'absolute-left': 'absolute-top q-mx-auto'"
            :style="{
              'font-size': clean ? '45px' : '60px',
              'margin-top': clean ? '5px' : '10px'
            }"
          />
          <span style="max-width: 50px;">
            {{ $t('movie.actions.watchTrailer') }}
          </span>
        </q-btn>
      </div>
    </div>
  </div>
</template>

<script>
import UserMovie from 'src/models/user-movie/UserMovie'

export default {
  name: 'MovieListButtons',
  configuration: ['currentSite'],
  inject: {
    order: { default: null }
  },
  props: {
    movie: {
      type: Object,
      required: true
    },
    size: {
      type: String,
      required: false,
      default: 'md'
    },
    gutterSize: {
      type: String,
      required: false,
      default: 'lg'
    },
    showUserButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    showTrailerButton: {
      type: Boolean,
      required: false,
      default: false
    },
    activeOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    hideRating: {
      type: Boolean,
      required: false,
      default: false
    },
    hideFavorite: {
      type: Boolean,
      required: false,
      default: false
    },
    preventAnimations: {
      type: Boolean,
      required: false,
      default: false
    },
    clean: {
      type: Boolean,
      required: false,
      default: false
    },
    showLabels: {
      type: Boolean,
      required: false,
      default: false
    },
    useDarkMode: {
      type: Boolean,
      required: false,
      default: false
    },
    iconOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    vertical: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['play-trailer', 'sign-in-requested'],
  data () {
    return {
      favoriteLoading: false,
      watchListLoading: false,
      ratingLoading: false,

      ratingMenu: false,

      forceHide: false
    }
  },
  computed: {
    favorited () {
      if (!this.loggedIn) return false
      if (!this.movie) return false

      return _.find(this.user.userMovies.data, { type: 'favorite', movieId: this.movie.id })
    },
    watchListed () {
      if (!this.loggedIn) return false
      if (!this.movie) return false

      return _.find(this.user.userMovies.data, { type: 'watch_list', movieId: this.movie.id })
    },
    rated () {
      if (!this.loggedIn) return false
      if (!this.movie) return false

      return _.find(this.user.userMovies.data, { type: 'rating', movieId: this.movie.id })
    },
    rating () {
      if (!this.loggedIn) return 0
      if (!this.movie) return 0

      const ratingUserMovie = _.find(this.user.userMovies.data, { type: 'rating', movieId: this.movie.id })
      if (!ratingUserMovie) return 0

      return ratingUserMovie.rating || 0
    },
    watched () {
      if (!this.loggedIn) return false
      if (!this.movie) return false

      return _.includes(this.user.watchedMovieIds, this.movie.id)
    },
    loggedIn () {
      if (this.$kiosk && this.order) return !!this.order.user

      return this.currentUser && this.currentUser.isRemote
    },
    user () {
      if (this.$kiosk && this.order) return this.order.user

      return this.currentUser
    }
  },
  watch: {
    async activeOnly () {
      // Trigger animation so buttons that are already visible animate instead of instantly changing position
      if (!this.activeOnly) {
        this.forceHide = true
        await this.$nextTick()
        this.forceHide = false
      }
    }
  },
  context: ['currentUser'],
  methods: {
    playTrailer () {
      this.$emit('play-trailer')
      this.$trackEvent(this.$customerEvents.MOVIE_PAGE_TRAILER_BUTTON, this.movie.trackingData)
    },
    toggleList: async function (type) {
      this.$trackEvent(
        this.$customerEvents.CLICKED_USER_MOVIE_BUTTON,
        { type, loggedIn: this.loggedIn, movieId: this.movie.id, movieName: this.movie.name }
      )
      if (!this.loggedIn) {
        this.$trackEvent(this.$customerEvents.SAW_SIGN_IN_TO_SAVE_MOVIE)
        this.$q.dialog({
          title: this.$i18n.t('movie.signInToSaveAMovie'),
          ok: {
            label: this.$i18n.t('auth.actions.registerOrLogin'),
            color: 'primary'
          },
          cancel: {
            label: this.$i18n.t('assertion.notNow'),
            color: 'secondary'
          }
        }).onOk(() => {
          if (this.$kiosk) {
            this.$emit('sign-in-requested')
            return
          }
          this.$auth.navigatePostAuth(this.$route.path)
          this.$router.push('/').catch(() => {})
          this.$router.push('/register')
        })

        return
      }

      if (type === 'favorite') {
        this.favoriteLoading = true
      } else if (type === 'watch_list') {
        this.watchListLoading = true
      } else if (type === 'rating') {
        this.ratingLoading = true
      } else {
        throw new Error('Unhandled type: ' + type)
      }

      let selected
      if (type === 'favorite') {
        selected = this.favorited
      } else if (type === 'watch_list') {
        selected = this.watchListed
      } else if (type === 'rating') {
        selected = this.rated
      } else {
        throw new Error('Unhandled type: ' + type)
      }

      this.$trackEvent(
        this.$customerEvents.TOGGLED_MOVIE_BUTTON,
        { ...this.movie.trackingData, value: !selected }
      )

      let result
      let errors
      let userMovie
      if (!selected) {
        // Add to list
        userMovie = new UserMovie({
          userId: this.user.id,
          movieId: this.movie.id,
          type
        })
        result = await userMovie.save()

        errors = result.errors
      } else {
        // Delete from list
        userMovie = selected
        result = await userMovie.delete()
        errors = result.errors
      }

      if (errors.length) {
        this.$mh.showErrors(errors)
        return
      }

      await this.user.fetch(true)

      if (type === 'favorite') {
        this.favoriteLoading = false
      } else if (type === 'watch_list') {
        this.watchListLoading = false
      } else if (type === 'rating') {
        this.ratingLoading = false
      } else {
        throw new Error('Unhandled type: ' + type)
      }
    },
    setRating: async function (rating) {
      this.$trackEvent(
        this.$customerEvents.RATE_MOVIE,
        this.movie.trackingData
      )

      this.ratingMenu = false
      this.ratingLoading = true

      if (!this.loggedIn) {
        this.$q.dialog({
          title: this.$i18n.t('movie.signInToSaveAMovie'),
          ok: {
            label: this.$i18n.t('auth.actions.registerOrLogin'),
            color: 'primary'
          },
          cancel: {
            label: this.$i18n.t('assertion.notNow'),
            color: 'secondary'
          }
        }).onOk(() => {
          this.$auth.navigatePostAuth(this.$route.path)
          this.$router.push('/').catch(() => {})
          this.$router.push('/register')
        })

        return
      }

      // Add or update rating
      const userMovie = new UserMovie({
        userId: this.user.id,
        movieId: this.movie.id,
        type: 'rating',
        rating
      })
      const result = await userMovie.save()
      const errors = result.errors

      if (errors.length) {
        this.$mh.showErrors(errors)
        return
      }

      await this.user.fetch(true)

      this.ratingLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.page {
  width: 100%;
  margin: -10px auto 0;
}

.watch-trailer-btn {
  max-width: 200px;
}

.buttons .q-btn {
  background: var(--q-primary);
}

.buttons .q-btn.clean {
  color: var(--q-primary);
}
.button-label {
  margin-left: 10px;

}

.show-label:hover {
  cursor: pointer;
}
.text-caption{
  line-height: 0.85rem;
  margin-top: 5px;
}
</style>

<style lang="scss">
.rating {
  z-index: 100;
  background: rgba(33, 33, 33, 0.5);
}

.q-tooltip {
  font-weight: bold;
  font-size: 12px;
  background-color: var(--q-secondary);
}

</style>
